import React, { useState, useRef } from 'react';
import { CourseConfiguration, Obstacle, ObstacleType, DRF_2025_QUOTAS, isSpecialObstacleAllowed } from '../types/drf-rules';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface CourseGridProps {
  config: CourseConfiguration;
  onGridClick: (x: number, y: number) => void;
  onObstacleMove: (obstacleId: string, newX: number, newY: number) => void;
  onObstacleRotate: (obstacleId: string) => void;
  onObstacleRotateCounterClockwise: (obstacleId: string) => void;
  onObstacleDelete: (obstacleId: string) => void;
  onObstacleNumberIncrease: (obstacleId: string) => void;
  onObstacleNumberDecrease: (obstacleId: string) => void;
  onObstacleHeightChange: (obstacleId: string, newHeight: number) => void;
  onObstacleSelect: (obstacleId: string | null) => void;
  selectedTool: string | null;
  showCoursePath?: boolean;
}

const CourseGrid: React.FC<CourseGridProps> = ({
  config,
  onGridClick,
  onObstacleMove,
  onObstacleRotate: _onObstacleRotate,
  onObstacleRotateCounterClockwise: _onObstacleRotateCounterClockwise,
  onObstacleDelete: _onObstacleDelete,
  onObstacleNumberIncrease: _onObstacleNumberIncrease,
  onObstacleNumberDecrease: _onObstacleNumberDecrease,
  onObstacleHeightChange: _onObstacleHeightChange,
  onObstacleSelect,
  selectedTool,
  showCoursePath = false
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);
  const [draggedObstacle, setDraggedObstacle] = useState<string | null>(null);
  const [selectedObstacle, setSelectedObstacle] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const gridSize = 1; // 1 meter grid
  
  // Dynamic scale calculation to fit arena on screen - improved scaling logic
  const maxWidth = 1200; // Maximum SVG width in pixels  
  const maxHeight = 800; // Maximum SVG height in pixels
  const scaleFromWidth = maxWidth / config.arenaWidth;
  const scaleFromHeight = maxHeight / config.arenaLength;
  
  // Use adaptive scaling that ensures good visibility for both dimensions
  const minScale = 3; // Reduced minimum scale to allow more scaling down for large arenas
  const maxScale = 15; // Maximum scale to avoid elements being too large
  
  // Calculate the limiting scale (smallest of the two)
  const limitingScale = Math.min(scaleFromWidth, scaleFromHeight);
  
  // Use the limiting scale but ensure minimum visibility
  let scale = Math.max(minScale, Math.min(maxScale, limitingScale));
  
  const svgWidth = config.arenaWidth * scale;
  const svgHeight = config.arenaLength * scale;
  
  // Fullscreen scaling for optimal viewing and export
  const modalHeaderHeight = 80; // Approximate height of header and padding
  const availableWidth = window.innerWidth * 0.9;
  const availableHeight = window.innerHeight - modalHeaderHeight;
  
  const fullscreenScale = isFullscreen ? Math.min(
    availableWidth / config.arenaWidth,
    availableHeight / config.arenaLength,
    12 // Maximum scale to keep things manageable
  ) : scale;
  
  const fullscreenSvgWidth = config.arenaWidth * fullscreenScale;
  const fullscreenSvgHeight = config.arenaLength * fullscreenScale;
  
  // PDF Export function
  const exportToPDF = async () => {
    if (!fullscreenContainerRef.current) return;
    
    setIsExporting(true);
    try {
      const canvas = await html2canvas(fullscreenContainerRef.current, {
        backgroundColor: '#F9FAFB',
        scale: 2, // High resolution
        useCORS: true
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: fullscreenSvgWidth > fullscreenSvgHeight ? 'landscape' : 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgAspectRatio = canvas.width / canvas.height;
      const pdfAspectRatio = pdfWidth / pdfHeight;
      
      let finalWidth, finalHeight;
      if (imgAspectRatio > pdfAspectRatio) {
        finalWidth = pdfWidth;
        finalHeight = pdfWidth / imgAspectRatio;
      } else {
        finalHeight = pdfHeight;
        finalWidth = pdfHeight * imgAspectRatio;
      }
      
      const x = (pdfWidth - finalWidth) / 2;
      const y = (pdfHeight - finalHeight) / 2;
      
      pdf.addImage(imgData, 'PNG', x, y, finalWidth, finalHeight);
      pdf.save(`springbane-${config.arenaWidth}x${config.arenaLength}m.pdf`);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Der opstod en fejl ved eksport til PDF');
    } finally {
      setIsExporting(false);
    }
  };
  
  const obstacleLength = 3.5; // 3.5 meters wide obstacles

  const quota = DRF_2025_QUOTAS[config.classCode];
  const currentObstacleCount = config.obstacles.filter(obstacle => 
    obstacle.type !== 'start' && obstacle.type !== 'finish'
  ).length;
  
  // Count obstacles by type (excluding start/finish for obstacle total, but including for placement limits)
  const allObstaclesByType = config.obstacles.reduce((acc, obstacle) => {
    acc[obstacle.type] = (acc[obstacle.type] || 0) + 1;
    return acc;
  }, {} as Record<ObstacleType, number>);
  
  const obstaclesByType = config.obstacles
    .filter(obstacle => obstacle.type !== 'start' && obstacle.type !== 'finish')
    .reduce((acc, obstacle) => {
      acc[obstacle.type] = (acc[obstacle.type] || 0) + 1;
      return acc;
    }, {} as Record<ObstacleType, number>);
  
  // Calculate remaining obstacles for each type (same logic as CourseDesigner)
  const calculateRemainingForType = (type: ObstacleType): number => {
    // For start and finish, use allObstaclesByType to include them in the count
    const currentCount = (type === 'start' || type === 'finish') 
      ? (allObstaclesByType[type] || 0)
      : (obstaclesByType[type] || 0);
    
    // Use DRF percentage distribution of TOTAL obstacles (not just regular slots)
    const totalSlots = quota.maxObstacles;
    
    // Calculate allocation for each type based on DRF percentage distribution of total
    // Note: Combinations are not placeable obstacles, so distribute that 5% among others
    switch (type) {
      case 'vertical':
        const verticalAllocation = Math.round(totalSlots * 0.50); // 50% of total (45% + part of combo)
        return Math.max(0, verticalAllocation - currentCount);
      case 'oxer':
        const oxerAllocation = Math.round(totalSlots * 0.43); // 43% of total (40% + part of combo)
        return Math.max(0, oxerAllocation - currentCount);
      case 'triple_bar':
        const tripleBarAllocation = Math.round(totalSlots * 0.07); // 7% of total (5% + part of combo)
        return Math.max(0, tripleBarAllocation - currentCount);
      case 'liverpool':
        if (!isSpecialObstacleAllowed(config.classCode, 'liverpool')) return 0;
        const liverpoolAllocation = Math.round(totalSlots * 0.05); // 5% of total
        return Math.max(0, liverpoolAllocation - currentCount);
      case 'open_water':
        if (!isSpecialObstacleAllowed(config.classCode, 'open_water') || config.isIndoor) return 0;
        const waterAllocation = Math.round(totalSlots * 0.05); // 5% of total
        return Math.max(0, waterAllocation - currentCount);
      case 'start':
        return Math.max(0, 1 - currentCount); // Only 1 start allowed
      case 'finish':
        return Math.max(0, 1 - currentCount); // Only 1 finish allowed
      default:
        return 0;
    }
  };
  
  // Check if specific obstacle type can be placed
  const canPlaceObstacleType = (type: ObstacleType): boolean => {
    // Check if special obstacles are allowed for this class
    if (!isSpecialObstacleAllowed(config.classCode, type)) {
      return false;
    }
    
    // Check if we have remaining slots for this specific type
    return calculateRemainingForType(type) > 0;
  };
  
  const isAtMaxObstacles = currentObstacleCount >= quota.maxObstacles;
  const selectedObstacleType = selectedTool as ObstacleType;
  const canPlaceSelectedType = selectedTool ? canPlaceObstacleType(selectedObstacleType) : false;
  const canPlaceObstacles = selectedTool && !isAtMaxObstacles && canPlaceSelectedType;

  const handleSvgClick = (event: React.MouseEvent<SVGSVGElement>) => {
    if (draggedObstacle) return;
    
    // Check if we clicked on an obstacle element or control
    const target = event.target as Element;
    const isObstacleElement = target.closest('[data-obstacle-control]') || 
                             target.hasAttribute('data-obstacle-control');
    
    if (isObstacleElement) return;
    
    // Deselect any selected obstacle when clicking on empty space
    onObstacleSelect(null);
    setSelectedObstacle(null);
    
    // Check if we've reached the maximum number of obstacles for this class
    if (currentObstacleCount >= quota.maxObstacles) {
      console.log(`Maximum obstacle count reached for ${config.classCode}: ${quota.maxObstacles}`);
      return;
    }
    
    // Check if we can place the selected obstacle type
    if (selectedTool && !canPlaceObstacleType(selectedObstacleType)) {
      console.log(`Cannot place more ${selectedTool} - limit reached`);
      return;
    }
    
    const rect = event.currentTarget.getBoundingClientRect();
    const x = Math.round((event.clientX - rect.left) / scale);
    const y = Math.round((event.clientY - rect.top) / scale);
    
    // Ensure click is within bounds
    if (x >= 0 && x <= config.arenaWidth && y >= 0 && y <= config.arenaLength) {
      onGridClick(x, y);
    }
  };

  const handleObstacleMouseDown = (event: React.MouseEvent, obstacleId: string) => {
    event.stopPropagation();
    event.preventDefault();
    setDraggedObstacle(obstacleId);
    setSelectedObstacle(obstacleId);
  };

  const handleObstacleClick = (event: React.MouseEvent, obstacleId: string) => {
    event.stopPropagation();
    event.preventDefault();
    setSelectedObstacle(obstacleId);
    onObstacleSelect(obstacleId);
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (!draggedObstacle || !svgRef.current) return;
    
    const rect = svgRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(config.arenaWidth, Math.round((event.clientX - rect.left) / scale)));
    const y = Math.max(0, Math.min(config.arenaLength, Math.round((event.clientY - rect.top) / scale)));
    
    onObstacleMove(draggedObstacle, x, y);
  };

  const handleMouseUp = () => {
    setDraggedObstacle(null);
  };

  // Touch event handlers for mobile support
  const handleObstacleTouchStart = (event: React.TouchEvent, obstacleId: string) => {
    event.stopPropagation();
    event.preventDefault();
    setDraggedObstacle(obstacleId);
    setSelectedObstacle(obstacleId);
  };

  const handleObstacleTouchMove = (event: React.TouchEvent) => {
    if (!draggedObstacle || !svgRef.current) return;
    
    event.preventDefault(); // Prevent scrolling
    const touch = event.touches[0];
    const rect = svgRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(config.arenaWidth, Math.round((touch.clientX - rect.left) / scale)));
    const y = Math.max(0, Math.min(config.arenaLength, Math.round((touch.clientY - rect.top) / scale)));
    
    onObstacleMove(draggedObstacle, x, y);
  };

  const handleTouchEnd = () => {
    setDraggedObstacle(null);
  };

  const handleSvgTouchStart = (event: React.TouchEvent<SVGSVGElement>) => {
    if (draggedObstacle) return;
    
    // Check if we touched an obstacle element or control
    const target = event.target as Element;
    const isObstacleElement = target.closest('[data-obstacle-control]') || 
                             target.hasAttribute('data-obstacle-control');
    
    if (isObstacleElement) return;
    
    // Deselect any selected obstacle when touching empty space
    onObstacleSelect(null);
    setSelectedObstacle(null);
    
    // Check if we've reached the maximum number of obstacles for this class
    if (currentObstacleCount >= quota.maxObstacles) {
      console.log(`Maximum obstacle count reached for ${config.classCode}: ${quota.maxObstacles}`);
      return;
    }
    
    // Check if we can place the selected obstacle type
    if (selectedTool && !canPlaceObstacleType(selectedObstacleType)) {
      console.log(`Cannot place more ${selectedTool} - limit reached`);
      return;
    }
    
    const touch = event.touches[0];
    const rect = event.currentTarget.getBoundingClientRect();
    const x = Math.round((touch.clientX - rect.left) / scale);
    const y = Math.round((touch.clientY - rect.top) / scale);
    
    // Ensure touch is within bounds
    if (x >= 0 && x <= config.arenaWidth && y >= 0 && y <= config.arenaLength) {
      onGridClick(x, y);
    }
  };

  const getObstacleColor = (type: ObstacleType): string => {
    switch (type) {
      case 'vertical': return '#8B5CF6';
      case 'oxer': return '#F59E0B';
      case 'triple_bar': return '#10B981';
      case 'liverpool': return '#3B82F6';
      case 'open_water': return '#06B6D4';
      case 'combination': return '#EF4444';
      case 'start': return '#22C55E';
      case 'finish': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const getLineEndpoints = (centerX: number, centerY: number, rotation: number, length: number) => {
    const radians = (rotation * Math.PI) / 180;
    const halfLength = length / 2;
    
    const x1 = centerX - halfLength * Math.cos(radians);
    const y1 = centerY - halfLength * Math.sin(radians);
    const x2 = centerX + halfLength * Math.cos(radians);
    const y2 = centerY + halfLength * Math.sin(radians);
    
    return { x1, y1, x2, y2 };
  };

  // Calculate distance between two points in meters
  const calculateDistance = (x1: number, y1: number, x2: number, y2: number): number => {
    return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
  };

  // Build realistic course path using approach/departure waypoints
  const buildCoursePath = (sequence: Obstacle[], scaleToUse: number, entryExitDist: number = 3, turnRadius: number = 6): string => {
    if (sequence.length < 2) return '';
    
    const waypoints: Array<{x: number, y: number, type: 'start' | 'approach' | 'center' | 'departure' | 'finish'}> = [];
    
    // Start point
    const startObstacle = sequence[0];
    waypoints.push({
      x: startObstacle.x,
      y: startObstacle.y,
      type: 'start'
    });
    
    // Process each jump (skip start, include finish)
    for (let i = 1; i < sequence.length; i++) {
      const obstacle = sequence[i];
      
      if (obstacle.type === 'finish') {
        // Just add finish point
        waypoints.push({
          x: obstacle.x,
          y: obstacle.y,
          type: 'finish'
        });
      } else {
        // Calculate direction vector for this jump
        const jumpDirection = (obstacle.rotation * Math.PI) / 180 - Math.PI/2; // Triangle direction
        const dirX = Math.cos(jumpDirection);
        const dirY = Math.sin(jumpDirection);
        
        // Approach point (before the jump)
        const approachX = obstacle.x - dirX * entryExitDist;
        const approachY = obstacle.y - dirY * entryExitDist;
        
        // Departure point (after the jump)  
        const departureX = obstacle.x + dirX * entryExitDist;
        const departureY = obstacle.y + dirY * entryExitDist;
        
        waypoints.push({
          x: approachX,
          y: approachY,
          type: 'approach'
        });
        waypoints.push({
          x: obstacle.x,
          y: obstacle.y,
          type: 'center'
        });
        waypoints.push({
          x: departureX,
          y: departureY,
          type: 'departure'
        });
      }
    }
    
    // Convert waypoints to smooth SVG path
    if (waypoints.length < 2) return '';
    
    let pathData = `M ${waypoints[0].x * scaleToUse} ${waypoints[0].y * scaleToUse}`;
    
    for (let i = 1; i < waypoints.length; i++) {
      const current = waypoints[i];
      const prev = waypoints[i - 1];
      const next = waypoints[i + 1];
      
      // Check if we need to smooth this corner
      if (next && i > 0 && i < waypoints.length - 1) {
        // Calculate vectors for incoming and outgoing segments
        const v1x = current.x - prev.x;
        const v1y = current.y - prev.y;
        const v2x = next.x - current.x;
        const v2y = next.y - current.y;
        
        // Calculate angle between segments
        const len1 = Math.sqrt(v1x * v1x + v1y * v1y);
        const len2 = Math.sqrt(v2x * v2x + v2y * v2y);
        
        if (len1 > 0 && len2 > 0) {
          const dot = (v1x * v2x + v1y * v2y) / (len1 * len2);
          const angle = Math.acos(Math.max(-1, Math.min(1, dot))); // Clamp to avoid NaN
          const angleDeg = (angle * 180) / Math.PI;
          
          // If corner is sharp (less than 150°), add smooth curve
          if (angleDeg < 150 && current.type !== 'center') {
            const curveRadius = Math.min(turnRadius, len1 * 0.3, len2 * 0.3);
            
            // Calculate curve start and end points
            const trimDist = curveRadius * Math.tan((Math.PI - angle) / 2);
            const trimStart = Math.min(trimDist, len1 * 0.8);
            const trimEnd = Math.min(trimDist, len2 * 0.8);
            
            const startX = current.x - (v1x / len1) * trimStart;
            const startY = current.y - (v1y / len1) * trimStart;
            const endX = current.x + (v2x / len2) * trimEnd;
            const endY = current.y + (v2y / len2) * trimEnd;
            
            // Line to curve start
            pathData += ` L ${startX * scaleToUse} ${startY * scaleToUse}`;
            
            // Quadratic curve through the corner
            pathData += ` Q ${current.x * scaleToUse} ${current.y * scaleToUse} ${endX * scaleToUse} ${endY * scaleToUse}`;
          } else {
            // Sharp corner or jump center - draw straight line
            pathData += ` L ${current.x * scaleToUse} ${current.y * scaleToUse}`;
          }
        } else {
          pathData += ` L ${current.x * scaleToUse} ${current.y * scaleToUse}`;
        }
      } else {
        // First, last, or insufficient data for smoothing
        pathData += ` L ${current.x * scaleToUse} ${current.y * scaleToUse}`;
      }
    }
    
    return pathData;
  };

  // Get the course sequence (start -> 1 -> 2 -> ... -> finish)
  const getCourseSequence = (): Obstacle[] => {
    const startObstacle = config.obstacles.find(o => o.type === 'start');
    const finishObstacle = config.obstacles.find(o => o.type === 'finish');
    
    // Get numbered obstacles and sort by number
    const numberedObstacles = config.obstacles
      .filter(o => o.number && o.type !== 'start' && o.type !== 'finish')
      .sort((a, b) => (a.number || 0) - (b.number || 0));
    
    const sequence: Obstacle[] = [];
    if (startObstacle) sequence.push(startObstacle);
    sequence.push(...numberedObstacles);
    if (finishObstacle) sequence.push(finishObstacle);
    
    return sequence;
  };

  // Calculate total path distance more accurately
  const calculatePathDistance = (sequence: Obstacle[]): number => {
    if (sequence.length < 2) return 0;
    
    let totalDistance = 0;
    
    for (let i = 0; i < sequence.length - 1; i++) {
      const from = sequence[i];
      const to = sequence[i + 1];
      
      // Calculate distance between jump centers
      const centerDistance = calculateDistance(from.x, from.y, to.x, to.y);
      
      // Add some extra for the curves (realistic estimation)
      const curveMultiplier = centerDistance < 15 ? 1.2 : 1.1; // Shorter distances have more curve
      totalDistance += centerDistance * curveMultiplier;
    }
    
    return totalDistance;
  };

  // Render the course path
  const renderCoursePath = () => {
    if (!showCoursePath) return null;
    
    const sequence = getCourseSequence();
    if (sequence.length < 2) return null;
    
    const pathData = buildCoursePath(sequence, scale);
    const totalDistance = calculatePathDistance(sequence);
    
    // Calculate segment distances and positions for labels
    const pathElements: JSX.Element[] = [];
    
    // Main path
    pathElements.push(
      <path
        key="main-path"
        d={pathData}
        fill="none"
        stroke="#DC2626"
        strokeWidth="3"
        strokeDasharray="8,4"
        strokeLinecap="round"
        strokeOpacity={0.8}
        style={{ pointerEvents: 'none' }}
      />
    );
    
    // Add distance labels and arrows for each segment
    for (let i = 0; i < sequence.length - 1; i++) {
      const from = sequence[i];
      const to = sequence[i + 1];
      
      // Calculate segment distance
      const segmentDistance = calculateDistance(from.x, from.y, to.x, to.y);
      const curveMultiplier = segmentDistance < 15 ? 1.2 : 1.1;
      const displayDistance = segmentDistance * curveMultiplier;
      
      // Combination check: Only between numbered jumps (not start/finish)
      const isCombination = (from.type !== 'start' && from.type !== 'finish' && 
                            to.type !== 'start' && to.type !== 'finish') &&
                           (config.animalType === 'horse' 
                             ? (displayDistance >= 6 && displayDistance <= 12)
                             : (displayDistance >= 5 && displayDistance <= 11));
      
      // Position label at midpoint between jumps
      const labelX = ((from.x + to.x) / 2) * scale;
      const labelY = ((from.y + to.y) / 2) * scale;
      
      // Distance label
      pathElements.push(
        <g key={`distance-${i}`} style={{ pointerEvents: 'none' }}>
          <rect
            x={labelX - 16}
            y={labelY - 8}
            width={32}
            height={16}
            fill="white"
            stroke="#DC2626"
            strokeWidth={1.5}
            rx={3}
            fillOpacity={0.95}
          />
          <text
            x={labelX}
            y={labelY + 3}
            textAnchor="middle"
            fontSize="9"
            fontWeight="bold"
            fill="#1F2937"
          >
            {displayDistance.toFixed(1)}m
          </text>
          {/* Combination label */}
          {isCombination && (
            <text
              x={labelX + 22}
              y={labelY + 3}
              textAnchor="start"
              fontSize="8"
              fontWeight="bold"
              fill="#DC2626"
            >
              Kombination
            </text>
          )}
        </g>
      );
      
      // Direction arrow (only for actual jumps, not finish)
      if (to.type !== 'finish') {
        const jumpDirection = (to.rotation * Math.PI) / 180 - Math.PI/2;
        const arrowDistance = 4;
        const arrowX = (to.x - Math.cos(jumpDirection) * arrowDistance) * scale;
        const arrowY = (to.y - Math.sin(jumpDirection) * arrowDistance) * scale;
        const arrowSize = 8;
        
        pathElements.push(
          <g key={`arrow-${i}`} style={{ pointerEvents: 'none' }}>
            <polygon
              points={`${arrowX},${arrowY} ${arrowX - arrowSize * Math.cos(jumpDirection - Math.PI/6)},${arrowY - arrowSize * Math.sin(jumpDirection - Math.PI/6)} ${arrowX - arrowSize * Math.cos(jumpDirection + Math.PI/6)},${arrowY - arrowSize * Math.sin(jumpDirection + Math.PI/6)}`}
              fill="#DC2626"
            />
          </g>
        );
      }
    }
    
    // Total distance label in corner
    pathElements.push(
      <g key="total-distance" style={{ pointerEvents: 'none' }}>
        <rect
          x={10}
          y={10}
          width={120}
          height={25}
          fill="white"
          stroke="#DC2626"
          strokeWidth={2}
          rx={4}
        />
        <text
          x={70}
          y={27}
          textAnchor="middle"
          fontSize="12"
          fontWeight="bold"
          fill="#DC2626"
        >
          Total: {totalDistance.toFixed(1)}m
        </text>
      </g>
    );
    
    return <g className="course-path">{pathElements}</g>;
  };

  const renderObstacle = (obstacle: Obstacle) => {
    const centerX = obstacle.x * scale;
    const centerY = obstacle.y * scale;
    const color = getObstacleColor(obstacle.type);
    const isSelected = selectedObstacle === obstacle.id;
    
    // Calculate line endpoints based on rotation
    const { x1, y1, x2, y2 } = getLineEndpoints(
      centerX, 
      centerY, 
      obstacle.rotation, 
      obstacleLength * scale
    );

    let strokeWidth = 4;
    let strokePattern = 'none';
    
    // Different line styles for different obstacle types
    switch (obstacle.type) {
      case 'vertical':
        strokeWidth = 6;
        break;
      case 'oxer':
        strokeWidth = 8;
        break;
      case 'triple_bar':
        strokeWidth = 10;
        break;
      case 'liverpool':
        strokeWidth = 8;
        strokePattern = '8,4';
        break;
      case 'open_water':
        strokeWidth = 12;
        strokePattern = '4,4';
        break;
      case 'combination':
        strokeWidth = 6;
        strokePattern = '6,6';
        break;
      case 'start':
        strokeWidth = 8;
        strokePattern = '12,4';
        break;
      case 'finish':
        strokeWidth = 8;
        strokePattern = '12,4';
        break;
    }

    return (
      <g key={obstacle.id}>
        {/* Larger invisible clickable area for easier selection */}
        <rect
          x={centerX - obstacleLength * scale / 2 - 15}
          y={centerY - 25}
          width={obstacleLength * scale + 30}
          height={50}
          fill="transparent"
          style={{ cursor: 'move' }}
          data-obstacle-control="obstacle-clickarea"
          onClick={(e) => handleObstacleClick(e, obstacle.id)}
          onMouseDown={(e) => handleObstacleMouseDown(e, obstacle.id)}
          onTouchStart={(e) => handleObstacleTouchStart(e, obstacle.id)}
        />

        {/* Main obstacle line */}
        <line
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={strokePattern}
          strokeLinecap="round"
          style={{ pointerEvents: 'none' }}
          data-obstacle-control="obstacle-line"
        />
        
        {/* Selection highlight */}
        {isSelected && (
          <line
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="#000"
            strokeWidth={strokeWidth + 4}
            strokeOpacity={0.3}
            strokeLinecap="round"
            data-obstacle-control="obstacle-highlight"
            style={{ pointerEvents: 'none' }}
          />
        )}

        {/* Oxer second bar */}
        {obstacle.type === 'oxer' && (
          <line
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={color}
            strokeWidth={4}
            strokeOpacity={0.6}
            strokeLinecap="round"
            data-obstacle-control="oxer-line"
            transform={`translate(${-Math.sin(obstacle.rotation * Math.PI / 180) * 8}, ${Math.cos(obstacle.rotation * Math.PI / 180) * 8})`}
            style={{ pointerEvents: 'none' }}
          />
        )}

        {/* Triple bar additional lines */}
        {obstacle.type === 'triple_bar' && (
          <g style={{ pointerEvents: 'none' }} data-obstacle-control="triple-bar-lines">
            <line
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={color}
              strokeWidth={6}
              strokeOpacity={0.7}
              strokeLinecap="round"
              transform={`translate(${-Math.sin(obstacle.rotation * Math.PI / 180) * 6}, ${Math.cos(obstacle.rotation * Math.PI / 180) * 6})`}
            />
            <line
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={color}
              strokeWidth={4}
              strokeOpacity={0.5}
              strokeLinecap="round"
              transform={`translate(${-Math.sin(obstacle.rotation * Math.PI / 180) * 12}, ${Math.cos(obstacle.rotation * Math.PI / 180) * 12})`}
            />
          </g>
        )}

        {/* Liverpool water indicator */}
        {obstacle.type === 'liverpool' && (
          <line
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="#06B6D4"
            strokeWidth={12}
            strokeOpacity={0.4}
            strokeLinecap="round"
            data-obstacle-control="liverpool-water"
            transform={`translate(${-Math.sin(obstacle.rotation * Math.PI / 180) * 10}, ${Math.cos(obstacle.rotation * Math.PI / 180) * 10})`}
            style={{ pointerEvents: 'none' }}
          />
        )}
        
        {/* Obstacle label - simplified */}
        <text
          x={centerX}
          y={centerY + 25}
          textAnchor="middle"
          fontSize="9"
          fill="#6B7280"
          fontWeight="normal"
          style={{ pointerEvents: 'none' }}
        >
          {obstacle.height}cm • {obstacle.rotation}°
        </text>
        
        {/* Obstacle type indicator - only for start/finish */}
        {(obstacle.type === 'start' || obstacle.type === 'finish') && (
          <text
            x={centerX}
            y={centerY - 15}
            textAnchor="middle"
            fontSize="9"
            fill={color}
            fontWeight="bold"
            style={{ pointerEvents: 'none' }}
          >
            {obstacle.type === 'start' ? 'START' : 'SLUT'}
          </text>
        )}

        {/* Obstacle number with triangle background (for actual jumps) */}
        {obstacle.number && (
          <>
            {/* Triangle background for number - rotated with obstacle */}
            <g transform={`translate(${centerX}, ${centerY}) rotate(${obstacle.rotation})`}>
              <polygon
                points="0,-15 -12,10 12,10"
                fill="white"
                stroke={color}
                strokeWidth={2}
                style={{ pointerEvents: 'none' }}
              />
            </g>
            
            {/* Number text - center of triangle, not rotated */}
            <text
              x={centerX}
              y={centerY + 3}
              textAnchor="middle"
              fontSize="12"
              fill={color}
              fontWeight="bold"
              style={{ pointerEvents: 'none' }}
            >
              {obstacle.number}
            </text>
          </>
        )}
      </g>
    );
  };

  return (
    <div className="relative">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Springbane ({config.arenaWidth}m × {config.arenaLength}m)
        </h3>
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-600">
            {isAtMaxObstacles ? (
              <span className="text-red-600 font-medium">
                Maks antal spring nået ({currentObstacleCount}/{quota.maxObstacles})
              </span>
            ) : selectedTool && !canPlaceSelectedType ? (
              <span className="text-red-600 font-medium">
                Maks {selectedTool} nået ({obstaclesByType[selectedObstacleType] || 0} placeret)
              </span>
            ) : selectedTool ? (
              <>
                {`Valgt værktøj: ${selectedTool} (${currentObstacleCount}/${quota.maxObstacles})`}
                {` • ${selectedTool}: ${calculateRemainingForType(selectedObstacleType)} tilbage`}
              </>
            ) : (
              'Vælg et værktøj for at placere spring'
            )}
          </div>
          <button
            onClick={() => setIsFullscreen(true)}
            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-1"
            title="Udvid & Eksporter bane"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 3H5C3.89543 3 3 3.89543 3 5V8M21 8V5C21 3.89543 20.1046 3 19 3H16M16 21H19C20.1046 21 21 20.1046 21 19V16M8 21H5C3.89543 21 3 20.1046 3 19V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15M7 10L12 15M12 15L17 10M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
      
      <div className="border border-gray-300 rounded-lg overflow-auto" style={{ maxWidth: '100%', touchAction: 'none' }}>
        <svg
          ref={svgRef}
          width={svgWidth}
          height={svgHeight}
          style={{ 
            cursor: canPlaceObstacles ? 'crosshair' : 'not-allowed', 
            display: 'block',
            touchAction: 'none'
          }}
          onClick={handleSvgClick}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleSvgTouchStart}
          onTouchMove={handleObstacleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Grid lines */}
          <defs>
            <pattern id="grid" width={scale * gridSize} height={scale * gridSize} patternUnits="userSpaceOnUse">
              <path
                d={`M ${scale * gridSize} 0 L 0 0 0 ${scale * gridSize}`}
                fill="none"
                stroke="#E5E7EB"
                strokeWidth="1"
              />
            </pattern>
          </defs>
          
          {/* Arena background */}
          <rect width={svgWidth} height={svgHeight} fill="#F9FAFB" />
          <rect width={svgWidth} height={svgHeight} fill="url(#grid)" />
          
          {/* Arena border */}
          <rect
            width={svgWidth}
            height={svgHeight}
            fill="none"
            stroke="#374151"
            strokeWidth="3"
          />
          
          {/* Distance markers */}
          {Array.from({ length: Math.floor(config.arenaWidth / 10) + 1 }, (_, i) => i * 10).map(distance => (
            <g key={`h-${distance}`}>
              <line
                x1={distance * scale}
                y1={0}
                x2={distance * scale}
                y2={svgHeight}
                stroke="#D1D5DB"
                strokeWidth="1"
                strokeDasharray="4,4"
              />
              <text
                x={distance * scale + 4}
                y={16}
                fontSize="12"
                fill="#6B7280"
              >
                {distance}m
              </text>
            </g>
          ))}
          
          {Array.from({ length: Math.floor(config.arenaLength / 10) + 1 }, (_, i) => i * 10).map(distance => (
            <g key={`v-${distance}`}>
              <line
                x1={0}
                y1={distance * scale}
                x2={svgWidth}
                y2={distance * scale}
                stroke="#D1D5DB"
                strokeWidth="1"
                strokeDasharray="4,4"
              />
              <text
                x={4}
                y={distance * scale + 16}
                fontSize="12"
                fill="#6B7280"
              >
                {distance}m
              </text>
            </g>
          ))}
          
          {/* Course Path - rendered BEFORE obstacles so they appear on top */}
          {renderCoursePath()}
          
          {/* Obstacles */}
          {config.obstacles.map(renderObstacle)}
        </svg>
      </div>
      
      <div className="mt-4 text-sm text-gray-600">
        <p>• Klik på gridet for at placere det valgte spring</p>
        <p>• Træk spring for at flytte dem</p>
        <p>• Klik på det blå ikon for at rotere et spring</p>
        <p>• Klik på det røde ikon for at slette et spring</p>
        {showCoursePath && (
          <p className="text-red-600 font-medium">
            • Rød stiplet linje viser hestens rute gennem banen med afstande mellem spring
          </p>
        )}
      </div>
      
      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-full max-h-full overflow-auto">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 sticky top-0 bg-white">
              <h3 className="text-xl font-bold text-gray-900">
                Springbane ({config.arenaWidth}m × {config.arenaLength}m) - Fuld visning
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={exportToPDF}
                  disabled={isExporting}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isExporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Eksporterer...
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15M7 10L12 15M12 15L17 10M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Download PDF
                    </>
                  )}
                </button>
                <button
                  onClick={() => setIsFullscreen(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Luk
                </button>
              </div>
            </div>
            
            <div className="p-4">
              <div ref={fullscreenContainerRef} className="bg-white p-4 rounded border" style={{ display: 'inline-block' }}>
                <svg
                  width={fullscreenSvgWidth}
                  height={fullscreenSvgHeight}
                  style={{ display: 'block' }}
                >
                  {/* Grid lines */}
                  <defs>
                    <pattern id="fullscreen-grid" width={fullscreenScale * gridSize} height={fullscreenScale * gridSize} patternUnits="userSpaceOnUse">
                      <path
                        d={`M ${fullscreenScale * gridSize} 0 L 0 0 0 ${fullscreenScale * gridSize}`}
                        fill="none"
                        stroke="#E5E7EB"
                        strokeWidth="1"
                      />
                    </pattern>
                  </defs>
                  
                  {/* Arena background */}
                  <rect width={fullscreenSvgWidth} height={fullscreenSvgHeight} fill="#F9FAFB" />
                  <rect width={fullscreenSvgWidth} height={fullscreenSvgHeight} fill="url(#fullscreen-grid)" />
                  
                  {/* Arena border */}
                  <rect
                    width={fullscreenSvgWidth}
                    height={fullscreenSvgHeight}
                    fill="none"
                    stroke="#374151"
                    strokeWidth="3"
                  />
                  
                  {/* Distance markers */}
                  {Array.from({ length: Math.floor(config.arenaWidth / 10) + 1 }, (_, i) => i * 10).map(distance => (
                    <g key={`fullscreen-h-${distance}`}>
                      <line
                        x1={distance * fullscreenScale}
                        y1={0}
                        x2={distance * fullscreenScale}
                        y2={fullscreenSvgHeight}
                        stroke="#D1D5DB"
                        strokeWidth="1"
                        strokeDasharray="4,4"
                      />
                      <text
                        x={distance * fullscreenScale + 4}
                        y={16}
                        fontSize="12"
                        fill="#6B7280"
                      >
                        {distance}m
                      </text>
                    </g>
                  ))}
                  
                  {Array.from({ length: Math.floor(config.arenaLength / 10) + 1 }, (_, i) => i * 10).map(distance => (
                    <g key={`fullscreen-v-${distance}`}>
                      <line
                        x1={0}
                        y1={distance * fullscreenScale}
                        x2={fullscreenSvgWidth}
                        y2={distance * fullscreenScale}
                        stroke="#D1D5DB"
                        strokeWidth="1"
                        strokeDasharray="4,4"
                      />
                      <text
                        x={4}
                        y={distance * fullscreenScale + 16}
                        fontSize="12"
                        fill="#6B7280"
                      >
                        {distance}m
                      </text>
                    </g>
                  ))}
                  
                  {/* Course Path - rendered BEFORE obstacles so they appear on top */}
                  {showCoursePath && (() => {
                    const sequence = getCourseSequence();
                    if (sequence.length < 2) return null;
                    
                    const pathData = buildCoursePath(sequence, fullscreenScale);
                    const totalDistance = calculatePathDistance(sequence);
                    
                    const pathElements: JSX.Element[] = [];
                    
                    // Main path
                    pathElements.push(
                      <path
                        key="fullscreen-main-path"
                        d={pathData}
                        fill="none"
                        stroke="#DC2626"
                        strokeWidth="3"
                        strokeDasharray="8,4"
                        strokeLinecap="round"
                        strokeOpacity={0.8}
                        style={{ pointerEvents: 'none' }}
                      />
                    );
                    
                    // Add distance labels and arrows for each segment
                    for (let i = 0; i < sequence.length - 1; i++) {
                      const from = sequence[i];
                      const to = sequence[i + 1];
                      
                      // Calculate segment distance
                      const segmentDistance = calculateDistance(from.x, from.y, to.x, to.y);
                      const curveMultiplier = segmentDistance < 15 ? 1.2 : 1.1;
                      const displayDistance = segmentDistance * curveMultiplier;
                      
                      // Combination check: Only between numbered jumps (not start/finish)
                      const isCombination = (from.type !== 'start' && from.type !== 'finish' && 
                                            to.type !== 'start' && to.type !== 'finish') &&
                                         (config.animalType === 'horse' 
                                           ? (displayDistance >= 6 && displayDistance <= 12)
                                           : (displayDistance >= 5 && displayDistance <= 11));
                      
                      // Position label at midpoint between jumps
                      const labelX = ((from.x + to.x) / 2) * fullscreenScale;
                      const labelY = ((from.y + to.y) / 2) * fullscreenScale;
                      
                      // Distance label
                      pathElements.push(
                        <g key={`fullscreen-distance-${i}`} style={{ pointerEvents: 'none' }}>
                          <rect
                            x={labelX - 16}
                            y={labelY - 8}
                            width={32}
                            height={16}
                            fill="white"
                            stroke="#DC2626"
                            strokeWidth={1.5}
                            rx={3}
                            fillOpacity={0.95}
                          />
                          <text
                            x={labelX}
                            y={labelY + 3}
                            textAnchor="middle"
                            fontSize="9"
                            fontWeight="bold"
                            fill="#1F2937"
                          >
                            {displayDistance.toFixed(1)}m
                          </text>
                          {/* Combination label */}
                          {isCombination && (
                            <text
                              x={labelX + 22}
                              y={labelY + 3}
                              textAnchor="start"
                              fontSize="8"
                              fontWeight="bold"
                              fill="#DC2626"
                            >
                              Kombination
                            </text>
                          )}
                        </g>
                      );
                      
                      // Direction arrow (only for actual jumps, not finish)
                      if (to.type !== 'finish') {
                        const jumpDirection = (to.rotation * Math.PI) / 180 - Math.PI/2;
                        const arrowDistance = 4;
                        const arrowX = (to.x - Math.cos(jumpDirection) * arrowDistance) * fullscreenScale;
                        const arrowY = (to.y - Math.sin(jumpDirection) * arrowDistance) * fullscreenScale;
                        const arrowSize = 8;
                        
                        pathElements.push(
                          <g key={`fullscreen-arrow-${i}`} style={{ pointerEvents: 'none' }}>
                            <polygon
                              points={`${arrowX},${arrowY} ${arrowX - arrowSize * Math.cos(jumpDirection - Math.PI/6)},${arrowY - arrowSize * Math.sin(jumpDirection - Math.PI/6)} ${arrowX - arrowSize * Math.cos(jumpDirection + Math.PI/6)},${arrowY - arrowSize * Math.sin(jumpDirection + Math.PI/6)}`}
                              fill="#DC2626"
                            />
                          </g>
                        );
                      }
                    }
                    
                    // Total distance label in corner
                    pathElements.push(
                      <g key="fullscreen-total-distance" style={{ pointerEvents: 'none' }}>
                        <rect
                          x={10}
                          y={10}
                          width={120}
                          height={25}
                          fill="white"
                          stroke="#DC2626"
                          strokeWidth={2}
                          rx={4}
                        />
                        <text
                          x={70}
                          y={27}
                          textAnchor="middle"
                          fontSize="12"
                          fontWeight="bold"
                          fill="#DC2626"
                        >
                          Total: {totalDistance.toFixed(1)}m
                        </text>
                      </g>
                    );
                    
                    return <g className="course-path">{pathElements}</g>;
                  })()}
                  
                  {/* Obstacles - render each obstacle with fullscreen scale */}
                  {config.obstacles.map(obstacle => {
                    const centerX = obstacle.x * fullscreenScale;
                    const centerY = obstacle.y * fullscreenScale;
                    const color = getObstacleColor(obstacle.type);
                    
                    // Calculate line endpoints based on rotation
                    const { x1, y1, x2, y2 } = getLineEndpoints(
                      centerX, 
                      centerY, 
                      obstacle.rotation, 
                      obstacleLength * fullscreenScale
                    );

                    let strokeWidth = 4;
                    let strokePattern = 'none';
                    
                    // Different line styles for different obstacle types
                    switch (obstacle.type) {
                      case 'vertical':
                        strokeWidth = 6;
                        break;
                      case 'oxer':
                        strokeWidth = 8;
                        break;
                      case 'triple_bar':
                        strokeWidth = 10;
                        break;
                      case 'liverpool':
                        strokeWidth = 8;
                        strokePattern = '8,4';
                        break;
                      case 'open_water':
                        strokeWidth = 12;
                        strokePattern = '4,4';
                        break;
                      case 'combination':
                        strokeWidth = 6;
                        strokePattern = '6,6';
                        break;
                      case 'start':
                        strokeWidth = 8;
                        strokePattern = '12,4';
                        break;
                      case 'finish':
                        strokeWidth = 8;
                        strokePattern = '12,4';
                        break;
                    }

                    return (
                      <g key={`fullscreen-${obstacle.id}`}>
                        {/* Main obstacle line */}
                        <line
                          x1={x1}
                          y1={y1}
                          x2={x2}
                          y2={y2}
                          stroke={color}
                          strokeWidth={strokeWidth}
                          strokeDasharray={strokePattern}
                          strokeLinecap="round"
                        />
                        
                        {/* Oxer second bar */}
                        {obstacle.type === 'oxer' && (
                          <line
                            x1={x1}
                            y1={y1}
                            x2={x2}
                            y2={y2}
                            stroke={color}
                            strokeWidth={4}
                            strokeOpacity={0.6}
                            strokeLinecap="round"
                            transform={`translate(${-Math.sin(obstacle.rotation * Math.PI / 180) * 8}, ${Math.cos(obstacle.rotation * Math.PI / 180) * 8})`}
                          />
                        )}

                        {/* Triple bar additional lines */}
                        {obstacle.type === 'triple_bar' && (
                          <g>
                            <line
                              x1={x1}
                              y1={y1}
                              x2={x2}
                              y2={y2}
                              stroke={color}
                              strokeWidth={6}
                              strokeOpacity={0.7}
                              strokeLinecap="round"
                              transform={`translate(${-Math.sin(obstacle.rotation * Math.PI / 180) * 6}, ${Math.cos(obstacle.rotation * Math.PI / 180) * 6})`}
                            />
                            <line
                              x1={x1}
                              y1={y1}
                              x2={x2}
                              y2={y2}
                              stroke={color}
                              strokeWidth={4}
                              strokeOpacity={0.5}
                              strokeLinecap="round"
                              transform={`translate(${-Math.sin(obstacle.rotation * Math.PI / 180) * 12}, ${Math.cos(obstacle.rotation * Math.PI / 180) * 12})`}
                            />
                          </g>
                        )}

                        {/* Liverpool water indicator */}
                        {obstacle.type === 'liverpool' && (
                          <line
                            x1={x1}
                            y1={y1}
                            x2={x2}
                            y2={y2}
                            stroke="#06B6D4"
                            strokeWidth={12}
                            strokeOpacity={0.4}
                            strokeLinecap="round"
                            transform={`translate(${-Math.sin(obstacle.rotation * Math.PI / 180) * 10}, ${Math.cos(obstacle.rotation * Math.PI / 180) * 10})`}
                          />
                        )}

                        {/* Obstacle label - simplified */}
                        <text
                          x={centerX}
                          y={centerY + 25}
                          textAnchor="middle"
                          fontSize="9"
                          fill="#6B7280"
                          fontWeight="normal"
                        >
                          {obstacle.height}cm • {obstacle.rotation}°
                        </text>
                        
                        {/* Obstacle type indicator - only for start/finish */}
                        {(obstacle.type === 'start' || obstacle.type === 'finish') && (
                          <text
                            x={centerX}
                            y={centerY - 15}
                            textAnchor="middle"
                            fontSize="9"
                            fill={color}
                            fontWeight="bold"
                          >
                            {obstacle.type === 'start' ? 'START' : 'SLUT'}
                          </text>
                        )}

                        {/* Obstacle number with triangle background (for actual jumps) */}
                        {obstacle.number && (
                          <>
                            {/* Triangle background for number - rotated with obstacle */}
                            <g transform={`translate(${centerX}, ${centerY}) rotate(${obstacle.rotation})`}>
                              <polygon
                                points="0,-15 -12,10 12,10"
                                fill="white"
                                stroke={color}
                                strokeWidth={2}
                              />
                            </g>
                            
                            {/* Number text - center of triangle, not rotated */}
                            <text
                              x={centerX}
                              y={centerY + 3}
                              textAnchor="middle"
                              fontSize="12"
                              fill={color}
                              fontWeight="bold"
                            >
                              {obstacle.number}
                            </text>
                          </>
                        )}
                      </g>
                    );
                  })}
                </svg>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseGrid;